import { createAsyncThunk } from "@reduxjs/toolkit";
import dayjs from "dayjs";
import isSameOrAfter from "dayjs/plugin/isSameOrAfter";
import utc from "dayjs/plugin/utc";

import { type ExerciseWithVideo } from "@fitness-app/data-models/entities/Exercise";
import { type ExerciseInWorkout, type WorkoutExercisesType } from "@fitness-app/data-models/entities/TrainingProgram";
import { replaceExerciseInWorkout } from "@fitness-app/utils/src/programs/replaceExerciseInWorkout";

import { type AsyncThunkCreator } from "../../../index";
import { traineeProgramActions } from "../../traineeProgram";
import { updateClientCurrentWorkout } from "../reducer";
import { TRAINEE_ACTIVITIES_REDUCER_NAME } from "../types";
import { saveWorkoutToSpecifiedDay } from "./saveWorkoutToSpecifiedDay";

type Payload = {
  id: string;
  activityId: string | null;
  exercise: ExerciseWithVideo;
  workoutPart: WorkoutExercisesType;
  exerciseToReplace: ExerciseInWorkout;
  replaceInProgram: boolean;
  workoutId: string;
};

dayjs.extend(isSameOrAfter);
dayjs.extend(utc);

export const updateExerciseToAlternative = createAsyncThunk<void, Payload, AsyncThunkCreator<string>>(
  `${TRAINEE_ACTIVITIES_REDUCER_NAME}/updateExerciseToAlternative`,
  async (payload, { getState, dispatch, extra: { analytics } }) => {
    const { currentWorkout: savedWorkout } = getState().traineeActivities.data;
    const { programDetails } = getState().traineeProgram;

    if (!savedWorkout) {
      throw new Error("currentWorkout is not defined");
    }

    if (!payload.activityId) {
      await dispatch(saveWorkoutToSpecifiedDay({ isMobile: true })).unwrap();
    }

    const { currentWorkout } = getState().traineeActivities.data;

    if (!currentWorkout) {
      throw new Error("currentWorkout is not defined");
    }

    // The swap semantics (set recreation, status reset, slot identity) live in the shared
    // replaceExerciseInWorkout — TRE-727, one implementation for the client flow and the advisor.
    const replaced = replaceExerciseInWorkout(currentWorkout, payload.exerciseToReplace.id, payload.exercise);

    if (!replaced) {
      throw new Error("Exercise to replace not found in the current workout");
    }

    dispatch(updateClientCurrentWorkout(replaced.workout));
    await dispatch(saveWorkoutToSpecifiedDay({ isMobile: true }));

    const workoutInProgramToUpdate = programDetails
      ? programDetails.workouts.find((savedWorkout) => savedWorkout.id === payload.workoutId)
      : null;

    if (programDetails?.programId && payload.replaceInProgram && workoutInProgramToUpdate) {
      const replacedInProgram = replaceExerciseInWorkout(
        workoutInProgramToUpdate,
        payload.exerciseToReplace.id,
        payload.exercise,
      );

      if (replacedInProgram) {
        await dispatch(
          traineeProgramActions.updateWorkoutInProgram({
            programId: programDetails.programId,
            workout: replacedInProgram.workout,
          }),
        );
      }
    }

    analytics.track("update_exercise_to_alternative", { from: payload.exerciseToReplace.id, to: payload.exercise.id });
  },
);
